import { type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/*
 * Card is a surface container that mirrors the prototype's panel surfaces.
 * It uses bg-surface-2 with a subtle border to lift content off the background.
 *
 * Sub-components are attached as named properties so consumers get a clean
 * compound API:
 *   <Card>
 *     <Card.Header>
 *       <Card.Title>…</Card.Title>
 *       <Card.Description>…</Card.Description>
 *     </Card.Header>
 *     <Card.Content>…</Card.Content>
 *     <Card.Footer>…</Card.Footer>
 *   </Card>
 */

/* ---- Root ----------------------------------------------------------------- */

function CardRoot({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'bg-surface-2 border border-border rounded-md',
        'flex flex-col',
        className,
      )}
      {...props}
    />
  );
}

/* ---- Header --------------------------------------------------------------- */

function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // The `[&:has(+_.card-footer)]:pb-6` rule gives Header its own bottom
  // padding only when Footer follows it directly. With a Content sibling
  // between them, Content's `py-6` provides the gap and we don't double
  // up. Without the rule, Header's text would sit flush against the
  // Footer's border-top divider.
  return (
    <div
      className={cn(
        'flex flex-col gap-1 px-6 pt-6',
        '[&:has(+_.card-footer)]:pb-6',
        className,
      )}
      {...props}
    />
  );
}

/* ---- Title ---------------------------------------------------------------- */

function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn(
        'font-display font-semibold text-lg text-text leading-tight',
        className,
      )}
      {...props}
    />
  );
}

/* ---- Description ---------------------------------------------------------- */

function CardDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn('text-sm text-text-muted leading-relaxed', className)}
      {...props}
    />
  );
}

/* ---- Content -------------------------------------------------------------- */

function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-6 py-6 flex-1', className)} {...props} />
  );
}

/* ---- Footer --------------------------------------------------------------- */

function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // The `card-footer` class is the CSS hook Header uses to know "I'm
  // followed by a Footer, give myself bottom padding". Don't rename it
  // without updating the rule on CardHeader above.
  return (
    <div
      className={cn(
        'card-footer',
        'flex items-center gap-3 px-6 pb-6 pt-6',
        'border-t border-border mt-auto',
        className,
      )}
      {...props}
    />
  );
}

/* ---- Public API ----------------------------------------------------------- */

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Title: CardTitle,
  Description: CardDescription,
  Content: CardContent,
  Footer: CardFooter,
});
